import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, map, Subject, Subscription } from 'rxjs';
import { SmartTableHeader, SmartTableHeaderAndData, SmartTableRow } from '../smart-table/smart-table.component';
import {
    PLATFORM_LICENSE_DATE_PROPERTIES,
    PLATFORM_LICENSE_OVERVIEW_IDS,
    PLATFORM_LICENSE_PROPERTIES_WITH_CURRENCY,
    PLATFORM_LICENSE_TABLE_HEADERS,
    PlatformLicenseKeyLabels,
    PlatformLicenseKeySuffixes,
    PlatformLicenseProperties
} from '../../constants-and-enums/platform-license-types';
import { getFilteredSmartTableRows, getPropertyAsString } from '../../utils';
import { Router } from '@angular/router';
import { PlatformLicenseEntity } from 'src/app/store/entity-models';
import { LicensePricingType } from '@iota/iota-management-portal-v4-client';
import { Store } from '@ngrx/store';
import { QueryParamsService } from '../../services/query-params.service';
import { readSearchTerm, SEARCH_DEBOUNCE_IN_MS, toSearchQueryParams } from '../../utils/search-url-state';

@Component({
    standalone: false,
    selector: 'app-platform-license-table',
    templateUrl: './platform-license-table.component.html',
    styleUrls: ['./platform-license-table.component.scss']
})
export class PlatformLicenseTableComponent implements OnInit, OnChanges, OnDestroy {
    private readonly queryParams = inject(QueryParamsService);

    @Input() platformLicenses: PlatformLicenseEntity[];
    platformLicensesSubject = new BehaviorSubject<PlatformLicenseEntity[]>([]);

    @Input() loading: boolean;
    @Input() tableHeaders = PLATFORM_LICENSE_TABLE_HEADERS;
    tableHeadersSubject = new BehaviorSubject<(keyof PlatformLicenseProperties)[]>(PLATFORM_LICENSE_TABLE_HEADERS);

    // For the selection of licenses, e.g. to override them
    @Input() licenseSelectionEnabled = false;
    // Embedded instances must not overwrite the host page's query params.
    @Input() persistFiltersInUrl = false;
    @Output() selectedLicenseId = new EventEmitter<string>();

    @Output() reload = new EventEmitter<void>();

    searchString = '';
    searchUpdated = new Subject<string>();
    searchSubject = new BehaviorSubject<{
        searchString: string;
    }>(undefined);
    searchSubscription: Subscription;

    tableHeaderAndData$ = combineLatest([this.platformLicensesSubject, this.tableHeadersSubject, this.searchSubject]).pipe(
        map(([allPatformLicenses, tableHeaders, searchParams]): SmartTableHeaderAndData => {
            const result: SmartTableHeaderAndData = {};

            const headers: SmartTableHeader[] = [
                ...tableHeaders.map((col) => ({
                    key: col,
                    label: PlatformLicenseKeyLabels?.[col]
                })),
                ...PLATFORM_LICENSE_OVERVIEW_IDS.map((idCol) => ({
                    key: idCol,
                    hidden: true
                }))
            ];

            const rows = allPatformLicenses.map((platformLicense) => {
                const row: SmartTableRow = {};

                headers.forEach(async (header) => {
                    const key = header.key;
                    const keyFromPlatformLicense = key as keyof PlatformLicenseProperties;
                    row[key] = {};

                    row[key].data = getPropertyAsString(platformLicense?.[key], {
                        formatNumberAsCurrency: PLATFORM_LICENSE_PROPERTIES_WITH_CURRENCY.includes(keyFromPlatformLicense)
                    });
                    row[key].suffix = PlatformLicenseKeySuffixes?.[key];
                    if (PLATFORM_LICENSE_DATE_PROPERTIES.includes(keyFromPlatformLicense)) {
                        row[key].isDate = true;
                    }

                    // If scale price license is selected, show the price of the first interval
                    if (
                        platformLicense.licensePricingType === LicensePricingType.SCALE_PRICE_LICENSE &&
                        keyFromPlatformLicense === 'currentlyEffectivePricingPriceMonthly' &&
                        platformLicense?.currentlyEffectivePricing?.priceIntervalsPerUser?.length > 0
                    ) {
                        row[key].prefix = $localize`:@@pricingFrom:From` + ' ';
                        row[key].data = getPropertyAsString(
                            platformLicense?.currentlyEffectivePricing?.priceIntervalsPerUser?.[0]?.priceMonthly,
                            { formatNumberAsCurrency: true }
                        );
                    }
                    if (
                        platformLicense.licensePricingType === LicensePricingType.SCALE_PRICE_LICENSE &&
                        keyFromPlatformLicense === 'currentlyEffectivePricingPriceYearly' &&
                        platformLicense?.currentlyEffectivePricing?.priceIntervalsPerUser?.length > 0
                    ) {
                        row[key].prefix = $localize`:@@pricingFrom:From` + ' ';
                        row[key].data = getPropertyAsString(
                            platformLicense?.currentlyEffectivePricing?.priceIntervalsPerUser?.[0]?.priceYearly,
                            { formatNumberAsCurrency: true }
                        );
                    }
                });

                return row;
            });

            result.rows = getFilteredSmartTableRows(rows, headers, searchParams?.searchString);
            result.headers = headers;

            return result;
        })
    );

    constructor(
        private store: Store,
        private router: Router
    ) {}

    ngOnInit(): void {
        if (this.persistFiltersInUrl) {
            this.searchString = readSearchTerm(this.queryParams.current());
        }
        this.searchSubject.next({ searchString: this.searchString });

        this.searchSubscription = this.searchUpdated
            .pipe(debounceTime(SEARCH_DEBOUNCE_IN_MS), distinctUntilChanged())
            .subscribe((searchString) => {
                this.searchString = searchString;
                this.applyFilters();
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.platformLicenses) {
            this.platformLicensesSubject.next(this.platformLicenses);
        }
        if (changes?.tableHeaders) {
            this.tableHeadersSubject.next(this.tableHeaders);
        }
    }

    ngOnDestroy(): void {
        this.searchUpdated.complete();
        this.searchSubject.complete();
        this.platformLicensesSubject.complete();
        this.tableHeadersSubject.complete();
        this.searchSubscription?.unsubscribe();
    }

    updateSearchString(event: Event & { target: HTMLInputElement }) {
        this.searchUpdated.next(event.target.value);
    }
    clearSearchString() {
        this.searchString = '';
        this.applyFilters();
    }

    applyFilters() {
        this.searchSubject.next({
            searchString: this.searchString
        });
        this.writeUrlState();
    }

    private writeUrlState(): void {
        if (!this.persistFiltersInUrl) {
            return;
        }
        this.queryParams.write(toSearchQueryParams({ search: this.searchString }));
    }

    selectPlatformLicense(row?: SmartTableRow) {
        const id = row?.['platformLicenseId']?.data;
        this.selectedLicenseId.emit(id);
    }

    rowClickAction(row?: SmartTableRow) {
        const id = row?.['platformLicenseId']?.data;

        if (this.licenseSelectionEnabled) {
            this.selectedLicenseId.emit(id);
        } else {
            this.router.navigate(['/administration/platform-license/' + id]);
        }
    }
}
